import React, { useEffect, useMemo } from "react";
import RoleToggle from "../../components/RoleToggle";
import Service from "./_components/Service";
import Weight from "./_components/Weight";
import TimeSlot from "./_components/TimeSlot";
import PriceMatrix from "./_components/PriceMatrix";
import ItemCategory from "./_components/ItemCategory";
import StorageTab from "./_components/StorageTab";
import { useSearchParams } from "react-router-dom";
import { useClientPermissions } from "../../apis/profileApis";
import { useSelector } from "react-redux";
import { selectClientPermissions } from "../../features/auth/authSlice";
import BoxesTab from "./_components/BoxesTab";
import MarketplaceTab from "./_components/MarketplaceTab";
import { useLanguage } from "../../context/LanguageContext";

interface TabConfig {
  key: string;
  label: string;
  component: React.ReactNode;
  requirePermission?: boolean;
  module?: string;
  operation?: "read";
}

const hasPermission = (
  permissions: ReturnType<typeof selectClientPermissions>,
  module: string,
  operation: "read",
): boolean => {
  if (!permissions) return false;
  return permissions.some(
    (p) => p.module === module && p.operations.includes(operation),
  );
};

const ShippingConfiguration: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isLoading, error } = useClientPermissions();
  const permissions = useSelector(selectClientPermissions);
  const { t } = useLanguage();

  const activeRole = searchParams.get("tab") || "Service";
  const tabs: TabConfig[] = useMemo(
    () => [
      {
        key: "Service",
        label: "Service",
        component: <Service />,
      },
      {
        key: "Weight",
        label: "Weight",
        component: <Weight />,
      },
      {
        key: "Time Slot",
        label: "Time Slot",
        component: <TimeSlot />,
      },
      {
        key: "Item Category",
        label: "Item Category",
        component: <ItemCategory />,
        requirePermission: true,
        module: "products",
        operation: "read",
      },
      {
        key: "Price Matrix",
        label: "Price Matrix",
        component: <PriceMatrix />,
      },
      {
        key: "Storage",
        label: "Storage",
        component: <StorageTab />,
      },
      {
        key: "Marketplace",
        label: "Marketplace",
        component: <MarketplaceTab />,
      },
      {
        key: "Boxes",
        label: "Boxes",
        component: <BoxesTab />,
      },
    ],
    [],
  );

  const visibleTabs = useMemo(() => {
    return tabs.filter((tab) => {
      if (!tab.requirePermission) return true;
      return hasPermission(permissions, tab.module!, tab.operation!);
    });
  }, [permissions, tabs]);

  useEffect(() => {
    const isAllowed = visibleTabs.some((tab) => tab.key === activeRole);
    if (!isLoading && visibleTabs.length > 0 && !isAllowed) {
      setSearchParams({ tab: visibleTabs[0].key }, { replace: true });
    }
  }, [isLoading, visibleTabs, activeRole, setSearchParams]);

  const handleRoleChange = (label: string) => {
    const tab = visibleTabs.find((tabItem) => tabItem.label === label || t(tabItem.label) === label);
    if (tab) setSearchParams({ tab: tab.key });
  };

  if (isLoading) {
    return (
      <div className="w-full px-6 pt-4 space-y-6">
        <div className="flex flex-col justify-center items-center space-y-6 h-64">
          <span className="text-sm text-gray-600">{t("Loading permissions...")}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full px-6 pt-4 space-y-6">
        <div className="flex flex-col justify-center items-center space-y-6 h-64 text-red-600">
          <span className="text-sm">{t("Failed to load permissions")}</span>
        </div>
      </div>
    );
  }

  if (visibleTabs.length === 0) {
    return (
      <div className="w-full px-6 pt-4 space-y-6">
        <div className="flex flex-col justify-center items-center space-y-6 h-64 text-gray-500">
          <span className="text-sm">
            {t("You don't have access to any shipping configuration.")}
          </span>
        </div>
      </div>
    );
  }

  const activeTab = visibleTabs.find((tab) => tab.key === activeRole);
  const activeComponent = activeTab?.component ?? visibleTabs[0].component;

  return (
    <div className="w-full px-6  space-y-6">
      <div className="flex justify-center items-center">
        <h1 className="text-2xl text-[#212121] tracking-[-0.5px]">
          {t("Shipping Configuration")}
        </h1>
      </div>

      <div className="[@media(min-width:1920px)]:w-8/12 [@media(min-width:1536px)]:w-10/12 w-full mx-auto py-0">
        <div className="flex justify-center pb-4">
          <div className="whitespace-nowrap overflow-x-auto no-scrollbar">
            <RoleToggle
              options={visibleTabs.map((tab) => t(tab.label))}
              active={t(activeRole)}
              onChange={handleRoleChange}
            />
          </div>
        </div>
        {activeComponent}
      </div>
    </div>
  );
};

export default ShippingConfiguration;
